import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Stack from "@mui/material/Stack";
import Tooltip, {TooltipProps} from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {styled} from "@mui/material/styles";
import {ReactElement, cloneElement, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {NoteSummary} from "crqlar-react-common/dist/models/note/noteSummary.model";
import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";

const TooltipCloseButton = styled("button")({
    appearance: "none",
    position: "absolute",
    top: "50%",
    right: 4,
    transform: "translateY(-50%)",
    width: 24,
    height: 24,
    padding: 0,
    border: 0,
    borderRadius: 2,
    background: "transparent",
    color: "inherit",
    cursor: "pointer",
    opacity: 0.8,
    "&::before, &::after": {
        content: "\"\"",
        position: "absolute",
        top: "50%",
        left: "50%",
        width: 13,
        height: 1.5,
        borderRadius: 1,
        backgroundColor: "currentColor",
    },
    "&::before": {
        transform: "translate(-50%, -50%) rotate(45deg)",
    },
    "&::after": {
        transform: "translate(-50%, -50%) rotate(-45deg)",
    },
    "&:hover": {
        opacity: 1,
        backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
    "&:focus-visible": {
        opacity: 1,
        outline: "2px solid currentColor",
        outlineOffset: 1,
    },
});

/**
 * Which notes popup is currently on screen, shared by every `NotesTooltip` on the page.
 *
 * Each popup is its own `Tooltip`, and `leaveDelay` deliberately keeps the one you are leaving up
 * long enough to move the pointer into it — it is interactive (scrolls, has a close button). Moving
 * between two adjacent triggers therefore showed the old popup alongside the new one for a moment.
 * Claiming this slot closes the previous popup the instant the next one opens.
 */
let activeNotesTooltipId: symbol | null = null;
const activeNotesTooltipListeners = new Set<() => void>();

const notifyActiveNotesTooltipChanged = () =>
    activeNotesTooltipListeners.forEach(listener => listener());

const claimActiveNotesTooltip = (id: symbol) => {
    if (activeNotesTooltipId === id) return;
    activeNotesTooltipId = id;
    notifyActiveNotesTooltipChanged();
};

/** Takes any other popup off screen without opening this one. */
const dismissOtherNotesTooltips = (id: symbol) => {
    if (activeNotesTooltipId === null || activeNotesTooltipId === id) return;
    activeNotesTooltipId = null;
    notifyActiveNotesTooltipChanged();
};

const releaseActiveNotesTooltip = (id: symbol) => {
    if (activeNotesTooltipId !== id) return;
    activeNotesTooltipId = null;
};

/**
 * What a Notes column can be handed.
 *
 * Plain strings are still accepted: several callers compose their own list, and the notes of an
 * older API build arrive without a classification. Those simply get no department badge.
 */
export type NotesElementNote = string | NoteSummary;

interface NormalizedNote {
    text: string;
    classification?: NoteSummary;
}

const normalizeNotes = (notes?: readonly NotesElementNote[]): NormalizedNote[] =>
    (notes ?? [])
        .map(note => (
            typeof note === "string"
                ? {text: note.trim()}
                : {text: note?.note?.trim() ?? "", classification: note}
        ))
        .filter(note => !!note.text);

const chainHandlers = <E, >(...handlers: (((event: E) => void) | undefined)[]) =>
    (event: E) => handlers.forEach(handler => handler?.(event));

interface NotesTooltipProps {
    notes?: readonly NotesElementNote[];
    placement?: TooltipProps["placement"];
    /**
     * Opens the tooltip when the trigger itself is clicked. Needed for triggers that have no other
     * click action (touch devices get no hover), but unwanted on triggers that already do something
     * on click, such as a whole guest card.
     */
    openOnTriggerClick?: boolean;
    children: ReactElement;
}

/**
 * The notes popup shared by every Notes column and by the floorplan arrivals cards: one bordered
 * box per note so several notes stay visually distinct.
 *
 * Renders `children` untouched when there is nothing to show, so callers can wrap a trigger that
 * must stay on screen regardless of whether the row has notes.
 */
export function NotesTooltip({
    notes,
    placement = "bottom-start",
    openOnTriggerClick = false,
    children,
}: NotesTooltipProps) {
    const {t} = useTranslation();
    const noteGroupIndex = useNoteGroupIndex();
    const [open, setOpen] = useState(false);
    const [hoverOpeningDisabled, setHoverOpeningDisabled] = useState(false);
    const idRef = useRef<symbol>();
    if (!idRef.current) idRef.current = Symbol("notesTooltip");
    const id = idRef.current;

    useEffect(() => {
        const listener = () => {
            if (activeNotesTooltipId !== id) setOpen(false);
        };
        activeNotesTooltipListeners.add(listener);
        return () => {
            activeNotesTooltipListeners.delete(listener);
            releaseActiveNotesTooltip(id);
        };
    }, [id]);

    const openTooltip = () => {
        claimActiveNotesTooltip(id);
        setOpen(true);
    };
    const closeTooltip = () => {
        releaseActiveNotesTooltip(id);
        setOpen(false);
    };

    const normalizedNotes = normalizeNotes(notes);
    const displayedNotes = [...normalizedNotes].reverse();

    if (!displayedNotes.length) {
        // No popup of our own, but arriving on this trigger still means the pointer has left the
        // previous one — otherwise a note-less card would sit under a neighbour's popup.
        return cloneElement(children, {
            onMouseOver: chainHandlers<React.MouseEvent>(
                children.props.onMouseOver,
                () => dismissOtherNotesTooltips(id),
            ),
        });
    }

    const tooltipContent = (
        <Box>
            <Box
                sx={{
                    position: "relative",
                    px: 1.5,
                    py: 1,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                }}
            >
                <Typography variant="subtitle1" fontWeight={600} sx={{pr: 4}}>
                    {t("All notes")} ({normalizedNotes.length})
                </Typography>
                <TooltipCloseButton
                    type="button"
                    aria-label={t("Close")}
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => {
                        event.stopPropagation();
                        setHoverOpeningDisabled(true);
                        closeTooltip();
                    }}
                />
            </Box>
            <Stack
                gap={0.75}
                sx={{
                    p: 1.5,
                    maxHeight: "min(320px, 50vh)",
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    bgcolor: "background.paper",
                }}
            >
                {displayedNotes.map((note, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.75,
                            px: 1,
                            py: 0.75,
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                        }}
                    >
                        <NoteGroupIcon note={note.classification} index={noteGroupIndex}/>
                        <Typography
                            variant="body1"
                            sx={{minWidth: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere"}}
                        >
                            {note.text}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        </Box>
    );

    const trigger = cloneElement(children, {
        "aria-expanded": openOnTriggerClick ? open : undefined,
        "aria-haspopup": openOnTriggerClick ? "true" : undefined,
        onMouseLeave: chainHandlers<React.MouseEvent>(
            children.props.onMouseLeave,
            () => setHoverOpeningDisabled(false),
        ),
        onBlur: chainHandlers<React.FocusEvent>(
            children.props.onBlur,
            () => setHoverOpeningDisabled(false),
        ),
        onClick: chainHandlers<React.MouseEvent>(
            children.props.onClick,
            openOnTriggerClick
                ? event => {
                    event.stopPropagation();
                    setHoverOpeningDisabled(false);
                    openTooltip();
                }
                : undefined,
        ),
        onKeyDown: chainHandlers<React.KeyboardEvent>(
            children.props.onKeyDown,
            event => {
                if (event.key === "Escape") {
                    setHoverOpeningDisabled(true);
                    closeTooltip();
                }
            },
        ),
    });

    return (
        <ClickAwayListener onClickAway={closeTooltip}>
            <Tooltip
                title={tooltipContent}
                open={open}
                onOpen={() => {
                    // Even when this trigger is latched shut, the pointer has left the previous
                    // trigger — that popup must still go.
                    if (hoverOpeningDisabled) {
                        dismissOtherNotesTooltips(id);
                        return;
                    }
                    openTooltip();
                }}
                onClose={closeTooltip}
                placement={placement}
                describeChild
                disableTouchListener
                disableInteractive={false}
                enterDelay={0}
                enterNextDelay={0}
                leaveDelay={200}
                TransitionProps={{timeout: 0}}
                PopperProps={{
                    modifiers: [
                        {name: "flip", enabled: false},
                        {name: "offset", options: {offset: [0, 6]}},
                    ],
                }}
                componentsProps={{
                    popper: {
                        sx: {
                            pointerEvents: "auto",
                        },
                    },
                    tooltip: {
                        sx: {
                            width: "min(360px, calc(100vw - 24px))",
                            maxWidth: "none",
                            p: 0,
                            overflow: "hidden",
                            bgcolor: "background.paper",
                            color: "text.primary",
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.16)",
                        },
                    },
                }}
            >
                {trigger}
            </Tooltip>
        </ClickAwayListener>
    );
}

interface NotesElementProps {
    notes?: readonly NotesElementNote[];
    detailsHref?: string;
    detailsLabel?: string;
    asPrint?: boolean;
}

export function NotesElement({
    notes,
    detailsHref,
    detailsLabel,
    asPrint = false,
}: NotesElementProps) {
    const noteGroupIndex = useNoteGroupIndex();
    const normalizedNotes = normalizeNotes(notes);
    const displayedNotes = [...normalizedNotes].reverse();
    const latestNote = displayedNotes[0];

    if (!latestNote) return null;

    if (asPrint) {
        return (
            <Stack gap={0.5} sx={{whiteSpace: "normal"}}>
                {displayedNotes.map((note, index) => (
                    <Typography key={index} variant="body2">{note.text}</Typography>
                ))}
            </Stack>
        );
    }

    const triggerContent = (
        <Box sx={{display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0}}>
            <NoteGroupIcon note={latestNote.classification} index={noteGroupIndex}/>
            <Typography
                component="span"
                variant="body1"
                sx={{
                    minWidth: 0,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    lineHeight: 1.5,
                }}
            >
                {latestNote.text}
            </Typography>
        </Box>
    );
    const triggerSx = {
        display: "inline-flex",
        width: "fit-content",
        maxWidth: "100%",
        minWidth: 0,
        verticalAlign: "top",
        textAlign: "left",
        borderRadius: 0.5,
        "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 1,
        },
    } as const;

    return (
        <NotesTooltip notes={notes} openOnTriggerClick={!detailsHref}>
            {detailsHref ? (
                <ButtonBase
                    component="a"
                    href={detailsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    disableRipple
                    aria-label={detailsLabel ? `${latestNote.text}. ${detailsLabel}` : latestNote.text}
                    onClick={event => event.stopPropagation()}
                    sx={triggerSx}
                >
                    {triggerContent}
                </ButtonBase>
            ) : (
                <ButtonBase
                    type="button"
                    disableRipple
                    sx={triggerSx}
                >
                    {triggerContent}
                </ButtonBase>
            )}
        </NotesTooltip>
    );
}
